import {
  AnnotationDoc,
  DocumentAnnotationPanel,
  DocumentPin,
} from "components/DocumentAnnotation/DocumentAnnotationPanel";
import { PinPopover } from "components/DocumentAnnotation/PinPopover";
import { QuestionSidebar } from "components/DocumentAnnotation/QuestionSidebar";
import { SidebarQuestion } from "components/DocumentAnnotation/QuestionCard";
import { Button } from "components/DesignSystem/Button/Button";
import { UploadFromSelectorModal } from "components/UploadFromSelectorModal/UploadFromSelectorModal";
import { FileType } from "components/FileInput/FileInput";
import { PlusIcon } from "components/icons/PlusIcon";
import ToolTip from "components/design/toolTip";
import { useModal } from "hooks/useModal";
import {
  createEmptyQuestion,
  DraftQuestion,
  filesToStagedDocs,
  StagedDocument,
} from "./ChatRequestAnswer";
import { useState } from "react";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";

type PendingClick = {
  x: number;
  y: number;
  docIndex: number;
  pageNumber: number;
};

type RequestComposeViewProps = {
  stagedDocs: StagedDocument[];
  onStagedDocsChange: (docs: StagedDocument[]) => void;
  questions: DraftQuestion[];
  onQuestionsChange: (questions: DraftQuestion[]) => void;
  groupId: string;
  errorQuestionIds?: string[];
  scrollToQuestionId?: string | null;
};

export const RequestComposeView = ({
  stagedDocs,
  onStagedDocsChange,
  questions,
  onQuestionsChange,
  groupId,
  errorQuestionIds,
  scrollToQuestionId,
}: RequestComposeViewProps) => {
  const [activeDocIndex, setActiveDocIndex] = useState(0);
  const [activePinId, setActivePinId] = useState<string | null>(null);
  const [pinModeId, setPinModeId] = useState<string | null>(null);
  const [pendingClick, setPendingClick] = useState<PendingClick | null>(null);

  const uploadModal = useModal();
  const isSmallScreen = useIsSmallScreen();
  // Both panes cannot share a phone screen, so mobile switches between them.
  // Defaults to the document so an uploaded file is reachable straight away.
  const [mobileTab, setMobileTab] = useState<"document" | "questions">(
    "document"
  );
  const displayedMobileTab = scrollToQuestionId ? "questions" : mobileTab;

  const annotationDocs: AnnotationDoc[] = stagedDocs.map((doc) => ({
    id: doc.id,
    name: doc.name,
    previewUrl: doc.previewUrl,
    fileType: doc.fileType,
  }));

  const pins: DocumentPin[] = questions.flatMap((q, index) => {
    if (q.docIndex === null || q.x === null || q.y === null) return [];
    return [
      {
        id: q.id,
        docIndex: q.docIndex,
        x: q.x,
        y: q.y,
        pageNumber: q.pageNumber ?? 1,
        number: index + 1,
        isActive: activePinId === q.id,
      },
    ];
  });

  const sidebarQuestions: SidebarQuestion[] = questions.map((q) => ({
    id: q.id,
    text: q.text,
    docIndex: q.docIndex,
    x: q.x,
    y: q.y,
  }));

  const activatePin = (id: string) => {
    setActivePinId(id);
    setTimeout(() => setActivePinId(null), 1200);
  };

  const handleDocumentClick = (
    x: number,
    y: number,
    docIndex: number,
    pageNumber: number
  ) => {
    if (pinModeId) {
      onQuestionsChange(
        questions.map((q) =>
          q.id === pinModeId ? { ...q, x, y, docIndex, pageNumber } : q
        )
      );
      setPinModeId(null);
      return;
    }
    setPendingClick({ x, y, docIndex, pageNumber });
  };

  const handlePopoverAdd = (text: string) => {
    if (!pendingClick) return;
    const newQuestion: DraftQuestion = {
      id: crypto.randomUUID(),
      text,
      docIndex: pendingClick.docIndex,
      x: pendingClick.x,
      y: pendingClick.y,
      pageNumber: pendingClick.pageNumber,
    };
    onQuestionsChange([...questions, newQuestion]);
    activatePin(newQuestion.id);
    setPendingClick(null);
  };

  const handlePopoverClose = () => setPendingClick(null);

  const handlePinClick = (pinId: string) => activatePin(pinId);

  const handleQuestionClick = (id: string) => {
    const q = questions.find((q) => q.id === id);
    if (q?.docIndex != null) {
      setActiveDocIndex(q.docIndex);
    }
    activatePin(id);
  };

  const handleDeleteQuestion = (id: string) => {
    onQuestionsChange(questions.filter((q) => q.id !== id));
    if (activePinId === id) setActivePinId(null);
    if (pinModeId === id) setPinModeId(null);
  };

  const handleAddQuestion = () => {
    onQuestionsChange([...questions, createEmptyQuestion()]);
  };

  const handleChangeQuestion = (id: string, text: string) => {
    onQuestionsChange(questions.map((q) => (q.id === id ? { ...q, text } : q)));
  };

  const handleRemoveAnnotation = (id: string) => {
    onQuestionsChange(
      questions.map((q) =>
        q.id === id
          ? { ...q, x: null, y: null, docIndex: null, pageNumber: null }
          : q
      )
    );
  };

  const handleUploadMoreDocs = (files: FileType[]) => {
    onStagedDocsChange([...stagedDocs, ...filesToStagedDocs(files)]);
  };

  const handleDocRemove = (index: number) => {
    onStagedDocsChange(stagedDocs.filter((_, i) => i !== index));

    onQuestionsChange(
      questions.map((q) => {
        if (q.docIndex === null) return q;
        if (q.docIndex === index)
          return { ...q, docIndex: null, x: null, y: null, pageNumber: null };
        if (q.docIndex > index) return { ...q, docIndex: q.docIndex - 1 };
        return q;
      })
    );

    if (activeDocIndex >= index && activeDocIndex > 0) {
      setActiveDocIndex(activeDocIndex - 1);
    }
  };

  const documentPane = annotationDocs.length > 0 && (
    <DocumentAnnotationPanel
      docs={annotationDocs}
      activeDocIndex={activeDocIndex}
      onTabChange={setActiveDocIndex}
      pins={pins}
      onPinClick={handlePinClick}
      onDocumentClick={handleDocumentClick}
      groupId={groupId}
      onDocRemove={handleDocRemove}
      headerExtra={
        <ToolTip text="Add new document">
          <Button size="xsmall" customType="icon" onClick={uploadModal.open}>
            <PlusIcon size="10" />
          </Button>
        </ToolTip>
      }
      pageOverlayExtra={(pageNumber) =>
        pendingClick && pendingClick.pageNumber === pageNumber ? (
          <PinPopover
            x={pendingClick.x}
            y={pendingClick.y}
            onClose={handlePopoverClose}
            onAdd={handlePopoverAdd}
          />
        ) : null
      }
    />
  );

  const isCompact = annotationDocs.length === 0;

  const questionsPane = (
    <QuestionSidebar
      questions={sidebarQuestions}
      activePinId={activePinId}
      docs={annotationDocs}
      onQuestionClick={handleQuestionClick}
      onDeleteQuestion={handleDeleteQuestion}
      onChangeQuestion={handleChangeQuestion}
      onRemoveAnnotation={handleRemoveAnnotation}
      onAddQuestion={handleAddQuestion}
      pinModeId={pinModeId}
      onEnterPinMode={setPinModeId}
      onExitPinMode={() => setPinModeId(null)}
      errorQuestionIds={errorQuestionIds}
      scrollToQuestionId={scrollToQuestionId}
      compact={isCompact}
      showHeader={!isCompact}
    />
  );

  const uploadSelector = uploadModal.isOpen && (
    <UploadFromSelectorModal
      isOpen={uploadModal.isOpen}
      close={uploadModal.close}
      onDrop={handleUploadMoreDocs}
      groupId={groupId}
      allowSingleUpload={false}
    />
  );

  if (isCompact) {
    return (
      <div className="t-w-full t-flex t-flex-col">
        {questionsPane}
        {uploadSelector}
      </div>
    );
  }

  if (isSmallScreen) {
    return (
      <div className="t-w-full t-flex-1 t-min-h-0 t-flex t-flex-col t-overflow-hidden">
        <div className="t-shrink-0 t-border-0 t-border-b t-border-solid t-border-neutral-10 t-px-4 t-py-2">
          <ToggleGroup
            type="single"
            size="small"
            value={displayedMobileTab}
            onValueChange={(value: "document" | "questions") =>
              value && setMobileTab(value)
            }
          >
            <ToggleGroupItem value="document" className="t-whitespace-nowrap">
              Document ({annotationDocs.length})
            </ToggleGroupItem>
            <ToggleGroupItem value="questions" className="t-whitespace-nowrap">
              Questions ({questions.length})
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div className="t-min-h-0 t-flex-1 t-overflow-hidden">
          {displayedMobileTab === "document" ? (
            <div className="t-h-full t-overflow-auto">{documentPane}</div>
          ) : (
            questionsPane
          )}
        </div>

        {uploadSelector}
      </div>
    );
  }

  return (
    <div className="t-w-full t-flex-1 t-min-h-0 t-flex t-flex-row t-overflow-hidden">
      <div className="t-overflow-auto t-h-full t-flex-1">{documentPane}</div>

      <div className="t-h-full t-w-[550px] t-border-0 t-border-l t-border-solid t-border-neutral-10 t-overflow-hidden t-shrink-0">
        {questionsPane}
      </div>

      {uploadSelector}
    </div>
  );
};
